import { useCallback, useEffect, useRef, useState } from "react";
import { api, handleSessionExpired, streamQuery, UnauthorizedError } from "@/api/client";
import type { ChatAttachment, ChatMessage, HistoryTurn, QueryDone } from "@/api/types";
import { toast } from "@/lib/toast";
import { notify } from "@/lib/notify";

function uid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function newThreadId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

export const THREAD_KEY = "isac.chat.thread";

// ── 세션 재인가 리로드 시 질문 보존/자동 재전송 ──
// 세션이 실제 만료된 상태에서 질문을 보내면 401 → handleSessionExpired() 가
// SSO 로그인으로 리다이렉트하고, IdP 세션이 살아있으면 조용히 재인가되어 페이지가
// 리로드된다(사용자에겐 '새로고침'으로 보임). 이때 보내려던 질문이 유실되지 않도록
// 리다이렉트 직전에 sessionStorage 에 보관하고, 리로드 후 Chat.tsx 가 자동 재전송한다.
export const PENDING_QUESTION_KEY = "isac.chat.pendingQuestion";
const PENDING_CONSUMED_KEY = "isac.chat.pendingConsumedAt";

// ── 받아쓰기 → 회의록 작성 요청 연계 ──
// Stt 페이지에서 "회의록 작성 요청" 클릭 시 잡 정보(jobId·파일명)를 보관하고 채팅("/")으로
// 이동한다. Chat.tsx 가 마운트 복원 완료 후 전문을 내려받아 meeting 스킬을 자동 실행한다.
// (전문 대신 jobId 만 저장 — 장시간 녹취 전문은 sessionStorage 용량을 위협하고,
//  세션 재인가 리로드가 끼어도 jobId 로 다시 받으면 되므로 유실이 없다)
export const PENDING_STT_MEETING_KEY = "isac.chat.pendingSttMeeting";

/** 보관된 질문을 재전송하기 직전에 호출 — 재전송이 또 401을 만나도 무한 재인가 루프 금지. */
export function markPendingConsumed(): void {
  try {
    sessionStorage.setItem(PENDING_CONSUMED_KEY, String(Date.now()));
  } catch {
    /* 무시 */
  }
}

function savePendingQuestion(p: { q: string; customer: string; modelId: number | null }): void {
  try {
    // 직전 30초 내에 자동 재전송된 질문이 다시 401이면 저장하지 않는다(루프 방지).
    const consumed = Number(sessionStorage.getItem(PENDING_CONSUMED_KEY) || 0);
    if (Date.now() - consumed < 30 * 1000) return;
    sessionStorage.setItem(PENDING_QUESTION_KEY, JSON.stringify({ ...p, ts: Date.now() }));
  } catch {
    /* 저장 실패 시 기존 동작(질문 유실)과 동일 — 치명적이지 않음 */
  }
}

// ── 첨부 표시(파일명·이미지 썸네일·텍스트 미리보기) ──────────────────
// 과거엔 사용자 말풍선에 "[meeting] 요청" 같은 내부 힌트만 표시됐다.
// 스킬 힌트를 한글 라벨로 바꾸고, 첨부 파일을 메시지에 실어 보여준다.
const SKILL_LABELS: Record<string, string> = {
  meeting: "회의록 작성",
  summary: "요약서 작성",
  translate: "번역서 작성",
  analyze: "분석서 작성",
  compare: "문서 비교",
  doc_fill: "양식 문서 작성",
};

const ATTACH_IMG_RE = /\.(png|jpe?g|gif|bmp|webp|tiff?)$/i;
const ATTACH_TEXT_RE = /\.(txt|csv|md|log)$/i;
const ATTACH_TEXT_PREVIEW_CHARS = 3000;

/** File[] → 말풍선 표시용 첨부 메타. 이미지는 object URL, 텍스트는 앞부분 내용. */
async function buildAttachments(files: File[]): Promise<ChatAttachment[]> {
  const out: ChatAttachment[] = [];
  for (const f of files) {
    const a: ChatAttachment = { name: f.name, size: f.size, kind: "file" };
    try {
      if (ATTACH_IMG_RE.test(f.name)) {
        a.kind = "image";
        a.url = URL.createObjectURL(f); // 세션(페이지) 수명 동안 유지 — revoke 생략
      } else if (ATTACH_TEXT_RE.test(f.name)) {
        a.kind = "text";
        const t = await f.text();
        a.preview =
          t.slice(0, ATTACH_TEXT_PREVIEW_CHARS) +
          (t.length > ATTACH_TEXT_PREVIEW_CHARS ? "\n…(이하 생략)" : "");
      }
    } catch {
      /* 미리보기 생성 실패 → 파일명 칩만 표시 */
    }
    out.push(a);
  }
  return out;
}

export function useChatStream() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [busy, setBusy] = useState(false);
  const [threadId, setThreadId] = useState<string>(
    () => localStorage.getItem(THREAD_KEY) || newThreadId(),
  );
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    localStorage.setItem(THREAD_KEY, threadId);
  }, [threadId]);

  const patch = useCallback((id: string, p: Partial<ChatMessage>) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...p } : m)));
  }, []);

  const applyDone = useCallback(
    (botId: string, d: QueryDone) => {
      patch(botId, {
        text: d.answer ?? "",
        streaming: false,
        progress: null,
        sources: d.sources ?? [],
        sourceMeta: d.source_meta ?? [],
        noAnswer: !!d.no_answer,
        escalation: d.escalation ?? "",
        sessionId: d.session_id ?? null,
        externalRequired: !!d.external_required,
        confirmToken: d.confirm_token ?? null,
        blocked: !!d.blocked,
      });
    },
    [patch],
  );

  const reauth = (pending?: { q: string; customer: string; modelId: number | null }) => {
    // 보내려던 질문이 있으면 리다이렉트 전에 보관 → 재인가 리로드 후 자동 재전송(Chat.tsx).
    if (pending?.q?.trim()) savePendingQuestion(pending);
    toast("로그인이 필요합니다.", "error");
    // 중앙 처리(handleSessionExpired) 경유 — 중복 리다이렉트 1회 가드 + search 파라미터 보존.
    // (과거엔 loginUrl(pathname)로 직접 이동해 가드를 우회했고 search 도 유실됐다)
    handleSessionExpired();
  };

  // 답변 완료 후 후속 추천 질문 비동기 로드(실패 무시)
  const fetchSuggest = useCallback(
    (botId: string, question: string, answer: string) => {
      if (!question.trim() || !answer.trim()) return;
      api
        .suggest(question, answer)
        .then((r) => {
          if (r.suggestions?.length) patch(botId, { suggestions: r.suggestions });
        })
        .catch(() => {});
    },
    [patch],
  );

  const send = useCallback(
    // modelId: 사용자 선택 질의 모델(chat_model.id, v3.9) — null 이면 서버 기본 동작
    async (question: string, customer: string, modelId: number | null = null) => {
      if (busy || !question.trim()) return;
      setBusy(true);
      const botId = uid();
      setMessages((prev) => [
        ...prev,
        { id: uid(), role: "user", text: question },
        { id: botId, role: "bot", text: "", streaming: true, startedAt: Date.now() },
      ]);

      const ac = new AbortController();
      abortRef.current = ac;
      let acc = "";
      let doneSeen = false;

      await streamQuery(
        question,
        customer,
        threadId,
        {
          onChunk: (t) => {
            acc += t;
            // 첫 토큰이 흐르기 시작하면 진행률 표시는 끈다(본문으로 전환).
            patch(botId, { text: acc, streaming: true, progress: null });
          },
          onProgress: (p) => {
            // 보고서 종합 등 장시간 작업: 본문 토큰이 아직 없을 때 진행률을 보여준다.
            if (!acc) patch(botId, { progress: p, streaming: true });
          },
          onDone: (d) => {
            doneSeen = true;
            applyDone(botId, d);
            if (!d.blocked && !d.external_required) fetchSuggest(botId, question, d.answer || acc);
          },
          onError: async (msg) => {
            if (msg === "unauthorized") return reauth({ q: question, customer, modelId });
            try {
              const d = await api.query(question, customer, threadId, false, modelId);
              doneSeen = true;
              applyDone(botId, d);
              if (!d.blocked && !d.external_required) fetchSuggest(botId, question, d.answer || "");
            } catch (e) {
              if (e instanceof UnauthorizedError) return reauth({ q: question, customer, modelId });
              patch(botId, {
                text: "답변을 가져오지 못했습니다. 잠시 후 다시 시도해 주세요.",
                streaming: false,
                error: true,
              });
            }
          },
        },
        ac.signal,
        false,
        modelId, // v3.9: 선택 모델 전달(null 이면 서버 기본 동작)
      );

      if (!doneSeen) patch(botId, { streaming: false });
      setBusy(false);
      abortRef.current = null;
    },
    [busy, threadId, patch, applyDone, fetchSuggest],
  );

  const confirmExternal = useCallback(
    async (botId: string, token: string, accept: boolean) => {
      patch(botId, { externalRequired: false, confirmToken: null });
      const replyId = uid();
      setMessages((prev) => [...prev, { id: replyId, role: "bot", text: "", streaming: true, startedAt: Date.now() }]);
      try {
        const d = accept ? await api.confirm(token) : await api.decline(token);
        applyDone(replyId, d);
      } catch (e) {
        if (e instanceof UnauthorizedError) return reauth();
        patch(replyId, { text: "처리에 실패했습니다.", streaming: false, error: true });
      }
    },
    [patch, applyDone],
  );

  // 참고문서 일부를 제외(deselect)하고 동일 질문 재생성 (웹 전용·비스트리밍)
  const regenerateWithSources = useCallback(
    // modelId: 사용자 선택 질의 모델(chat_model.id) — null 이면 서버 자동 라우팅 (2026-09-30)
    async (question: string, customer: string, excludeUrls: string[], modelId: number | null = null) => {
      if (busy || !question.trim() || excludeUrls.length === 0) return;
      setBusy(true);
      const botId = uid();
      setMessages((prev) => [
        ...prev,
        { id: uid(), role: "user", text: `↻ 참고문서 ${excludeUrls.length}건 제외하고 다시 답변` },
        { id: botId, role: "bot", text: "", streaming: true, startedAt: Date.now() },
      ]);
      try {
        const d = await api.regenerate(question, customer, threadId, excludeUrls, modelId);
        applyDone(botId, d);
        if (!d.blocked && !d.external_required) fetchSuggest(botId, question, d.answer || "");
      } catch (e) {
        if (e instanceof UnauthorizedError) return reauth();
        patch(botId, { text: "재생성에 실패했습니다. 잠시 후 다시 시도해 주세요.", streaming: false, error: true });
      } finally {
        setBusy(false);
      }
    },
    [busy, threadId, patch, applyDone, fetchSuggest],
  );

  const setFeedback = useCallback(
    (id: string, fb: 1 | -1) => patch(id, { feedback: fb }),
    [patch],
  );

  const setBookmark = useCallback(
    (id: string, bookmarkId: number | null) => patch(id, { bookmarkId }),
    [patch],
  );

  // 첨부(다중) + 지시 → 스킬 실행(회의록·요약·비교 등). qa 판정 시 일반 질의로 폴백.
  // sourceQueryId: 양식 문서 작성(doc_fill)이 질문 없이 호출될 때 채울 '직전 답변'의 query_id
  //   (Composer 경로는 마지막 bot 턴, 답변 아래 '양식으로 문서 만들기' 버튼은 그 답변).
  const runSkill = useCallback(
    async (files: File[], customer: string, text: string, hint = "", modelId: number | null = null, hwocr = false,
           sourceQueryId = "") => {
      if (busy) return;
      setBusy(true);
      const botId = uid();
      // 내부 힌트("[meeting] 요청") 대신 한글 라벨 표시 + 첨부는 별도 칩/미리보기로.
      const label = hint ? (SKILL_LABELS[hint] ?? hint) : "";
      const q = (text || "").trim() || (label ? `${label} 요청` : "첨부 파일 분석 요청");
      const attachments = await buildAttachments(files);
      setMessages((prev) => [
        ...prev,
        { id: uid(), role: "user", text: q, attachments },
        { id: botId, role: "bot", text: "", streaming: true, startedAt: Date.now() },
      ]);
      try {
        const res = await api.skill(files, text, threadId, hint, customer, sourceQueryId, modelId);
        if (res.status === "route_to_qa") {
          // 일반 Q&A → 첨부를 세션문서로 올린 뒤 스트리밍 질의.
          // v3.8: 업로드 실패를 사용자에게 알리고, 전부 실패하면 질의를 중단한다
          //       (첨부를 못 본 채 지식저장소만으로 오답하는 것 방지).
          let uploaded = 0;
          const failed: string[] = [];
          for (const f of files) {
            try {
              // hwocr: '손글씨·저품질 스캔' 체크 시 서버가 HWOCR(LLM 비전)로 강제 판독
              await api.upload(f, threadId, hwocr);
              uploaded++;
            } catch {
              failed.push(f.name);
            }
          }
          if (failed.length > 0) {
            toast(`첨부 처리 실패: ${failed.join(", ")} (지원하지 않는 형식이거나 텍스트를 추출하지 못했습니다)`, "error");
          }
          if (uploaded === 0) {
            patch(botId, {
              text: "첨부 파일에서 텍스트를 추출하지 못해 질문을 진행하지 않았습니다.\n파일 형식(pdf·docx·xlsx·pptx·txt·csv·이미지)을 확인하고 다시 첨부해 주세요.",
              streaming: false,
              error: true,
            });
            return;
          }
          let acc = "";
          let done = false;
          // 질문 없이 첨부만 전송한 경우 기본 질의로 대체 —
          // BFF ChatQueryIn 검증(question min_length=1)에 빈 질문이 422로
          // 거부되어 "답변을 가져오지 못했습니다"가 뜨던 문제 (2026-07-21)
          const effQ = text.trim() || "방금 업로드한 문서의 핵심 내용을 정리해줘";
          await streamQuery(
            effQ,
            customer,
            threadId,
            {
              onChunk: (t) => {
                acc += t;
                patch(botId, { text: acc, streaming: true });
              },
              onDone: (d) => {
                done = true;
                applyDone(botId, d);
              },
              onError: async () => {
                try {
                  const d = await api.query(effQ, customer, threadId, true, modelId);
                  done = true;
                  applyDone(botId, d);
                } catch {
                  patch(botId, { text: "답변을 가져오지 못했습니다.", streaming: false, error: true });
                }
              },
            },
            undefined,
            true, // sdoc_hint — 첨부 직후 질의: 세션문서 참조 강제
            modelId, // v3.9: 선택 모델 전달
          );
          if (!done) patch(botId, { streaming: false });
        } else if (res.success) {
          patch(botId, {
            text: res.answer || `${res.label ?? "처리"} 완료`,
            streaming: false,
            downloads: res.downloads ?? [],
            sessionId: res.query_id ?? null,
          });
          // 처리에 시간이 걸려 사용자가 다른 탭을 보고 있으면 완료 알림
          if (document.hidden) {
            notify(`${res.label ?? "작업"} 완료`, (res.downloads?.length ?? 0) > 0 ? "다운로드가 준비되었습니다." : undefined);
          }
        } else {
          patch(botId, {
            text: `${res.label ?? "처리"} 실패: ${res.error || res.answer || "오류"}`,
            streaming: false,
            error: true,
          });
        }
      } catch (e) {
        if (e instanceof UnauthorizedError) return reauth();
        patch(botId, { text: "처리 중 오류가 발생했습니다.", streaming: false, error: true });
      } finally {
        setBusy(false);
      }
    },
    [busy, threadId, patch, applyDone],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setMessages((prev) => prev.map((m) => (m.streaming ? { ...m, streaming: false } : m)));
    setBusy(false);
  }, []);

  // 새 대화 시작 — 새 thread + 화면 비움
  const newThread = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setThreadId(newThreadId());
    setMessages([]);
    setBusy(false);
  }, []);

  // 이력에서 특정 대화 불러오기
  const loadThread = useCallback((id: string, turns: HistoryTurn[]) => {
    abortRef.current?.abort();
    abortRef.current = null;
    setThreadId(id);
    const msgs: ChatMessage[] = [];
    for (const t of turns) {
      if (t.question)
        msgs.push({
          id: uid(),
          role: "user",
          text: t.question,
          // 첨부 원본(서버 보관본) 복원 — 파일명 칩·이미지·내용 보기(지연 로드)
          attachments: (t.attachments ?? []).map((a) => ({
            name: a.name,
            size: a.size,
            kind: a.kind,
            url: a.url,
            token: a.token,
          })),
        });
      msgs.push({
        id: uid(),
        role: "bot",
        text: t.answer || "",
        streaming: false,
        sessionId: t.query_id || null,
        // 스킬 산출물 다운로드 버튼 복원 — 새로고침·이력 열람에서도 다시 받을 수 있게
        downloads: t.downloads ?? [],
      });
    }
    setMessages(msgs);
    setBusy(false);
  }, []);

  return {
    messages,
    busy,
    threadId,
    send,
    runSkill,
    stop,
    newThread,
    loadThread,
    confirmExternal,
    setFeedback,
    setBookmark,
    regenerateWithSources,
    reset: newThread,
  };
}
